import React from 'react';
import { motion } from 'framer-motion';
interface SyllableBlocksProps {
  word: string;
  syllables: string[];
  types: ('open' | 'closed')[];
  /**
   * Index (single) or indices (array) of syllable block(s) to highlight.
   * Accepts both shapes for didactic flexibility:
   *  - `0`        → highlight first syllable only
   *  - `[0, 1]`   → highlight multiple syllables (e.g. open + gesloten side-by-side)
   */
  highlight?: number | number[];
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
const isSyllableHighlighted = (highlight: number | number[] | undefined, index: number): boolean => {
  if (highlight === undefined) return false;
  if (Array.isArray(highlight)) return highlight.includes(index);
  return highlight === index;
};
/**
 * SyllableBlocks - Word syllable breakdown visual
 *
 * Shows word broken into syllables with open/closed indication.
 * Used for SPR (Spelling Regels) module sound group questions.
 *
 * stimulus mode: compact — word + syllable blocks only
 * support mode: full educational panel with explanations
 */
export function SyllableBlocks({
  word,
  syllables,
  types,
  highlight,
  label,
  mode = 'support'
}: SyllableBlocksProps) {
  // Find vowels in each syllable
  const getVowels = (syllable: string): number[] => {
    const vowels = ['a', 'e', 'i', 'o', 'u'];
    const positions: number[] = [];
    syllable.split('').forEach((char, index) => {
      if (vowels.includes(char.toLowerCase())) {
        positions.push(index);
      }
    });
    return positions;
  };
  // ── STIMULUS MODE: compact visual only ──────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-13899" data-mp="234ybvyotoe5r">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200" data-id="element-13900" data-mp="vq3cfzl3mrxb">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-13901" data-mp="t4vnaulr8ylb">
              <span className="text-xs font-bold text-green-800" data-id="element-13902" data-mp="t55eglijdbsl">{label}</span>
            </div>}

          {/* Complete Word - compact */}
          <div className="mb-3 text-center" data-id="element-13903" data-mp="j6oi190tw4z5">
            <span className="text-lg font-black text-slate-800" data-id="element-13904" data-mp="j5wn80f22b9x">{word}</span>
          </div>

          {/* Syllable Blocks - compact */}
          <div className="flex justify-center items-center gap-2 flex-wrap" data-id="element-13905" data-mp="o7c5xltrg5p5">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = isSyllableHighlighted(highlight, index);
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 10
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.1,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-13906" data-mp="5ei0bii11pxl" data-mpi={index}>
                  <div className={`
                    relative px-4 py-2.5 rounded-xl border-3 shadow-md
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-3 ring-yellow-300 scale-105' : ''}
                  `} data-id="element-13907" data-mp="3hzouuxzgboea">
                    <div className="flex gap-0.5" data-id="element-13908" data-mp="4j7pa30gc65n">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    const isLastLetter = charIndex === syllable.length - 1;
                    return <span key={charIndex} className={`
                              text-xl font-black
                              ${isVowel ? 'text-white drop-shadow-md' : 'text-white/90'}
                              ${isLastLetter ? isVowel ? 'ring-2 ring-green-200 bg-green-600/40 rounded px-0.5' : 'ring-2 ring-red-200 bg-red-600/40 rounded px-0.5' : ''}
                            `} data-id="element-13909" data-mp="9knbhyed8ipm" data-mpi={charIndex}>
                            {char}
                          </span>;
                  })}
                    </div>
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-sm" data-id="element-13910" data-mp="jmqxbg8tbz00">
                      <span className="text-[10px] font-bold" data-id="element-13911" data-mp="onewip773m5b">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>
                  <div className={`
                    mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-13912" data-mp="1zzfqqjmtw2sg">
                    {type === 'open' ? 'Open' : 'Gesloten'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-13913" data-mp="2wsnuzc52qe3c">
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg" data-id="element-13914" data-mp="2rudzf0fftbh7">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-13915" data-mp="2mwkt87e4whgx">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-green-200 shadow-sm" data-id="element-13916" data-mp="2mxnh06g5u3sm">
            <span className="text-2xl" data-id="element-13917" data-mp="l3elke4oc9i5">🔤</span>
            <div className="text-left" data-id="element-13918" data-mp="l4pjvyfcop1v">
              <div className="text-sm font-bold text-green-900" data-id="element-13919" data-mp="2fkehxm74evtr">
                {label || 'Lettergrepen Verdeling'}
              </div>
              <div className="text-xs text-green-600 font-semibold" data-id="element-13920" data-mp="2kbmu2pzu4vam">
                Open en Gesloten Lettergrepen
              </div>
            </div>
          </div>
        </div>

        {/* Complete Word */}
        <div className="mb-6 text-center" data-id="element-13921" data-mp="2all9472s3szx">
          <div className="inline-flex flex-col items-center gap-2 bg-white/80 rounded-xl px-6 py-4 border border-slate-200 shadow-sm" data-id="element-13922" data-mp="2yavyytilc2za">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-13923" data-mp="338ea8qk2yuag">
              Heel Woord
            </div>
            <div className="text-3xl font-black text-slate-800" data-id="element-13924" data-mp="2pc0srhfjqsvz">{word}</div>
          </div>
        </div>

        {/* Syllable Blocks */}
        <div className="mb-6" data-id="element-13925" data-mp="1dv14cwwbky78">
          <div className="flex justify-center items-center gap-3 flex-wrap" data-id="element-13926" data-mp="1dtzqkknx5xlt">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = isSyllableHighlighted(highlight, index);
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 20
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.15,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-13927" data-mp="15fv1959szanj" data-mpi={index}>
                  {/* Syllable Block */}
                  <div className={`
                    relative px-6 py-4 rounded-2xl border-4 shadow-xl
                    transition-all duration-300
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-4 ring-yellow-300 scale-110' : ''}
                  `} data-id="element-13928" data-mp="wl6u19ko3iw3">
                    {/* Letters */}
                    <div className="flex gap-1" data-id="element-13929" data-mp="3ogs7lzeh3p6d">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    const isLastLetter = charIndex === syllable.length - 1;
                    return <span key={charIndex} className={`
                              text-2xl md:text-3xl font-black
                              ${isVowel ? 'text-white drop-shadow-lg' : 'text-white/90'}
                              ${isLastLetter ? isVowel ? 'ring-2 ring-green-200 bg-green-600/40 rounded px-1' : 'ring-2 ring-red-200 bg-red-600/40 rounded px-1' : ''}
                            `} data-id="element-13930" data-mp="6adolm1ckp14" data-mpi={charIndex}>
                            {char}
                          </span>;
                  })}
                    </div>

                    {/* Type indicator */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-md" data-id="element-13931" data-mp="20ihfu5ns0c2b">
                      <span className="text-xs font-bold" data-id="element-13932" data-mp="1vmiw4vde7i88">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>

                  {/* Label */}
                  <div className={`
                    mt-2 px-3 py-1 rounded-lg text-xs font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-13933" data-mp="2jhvf9349yf07">
                    {type === 'open' ? 'Open (lange klank)' : 'Gesloten (korte klank)'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-13934" data-mp="2bzfsvm1m08h3">
          {/* What do you see? */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-13935" data-mp="2c0qsrarxow2x">
            <div className="flex items-start gap-3" data-id="element-13936" data-mp="27w9fzvjzrj1n">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-13937" data-mp="2lx2869mtm21l">
                <span className="text-blue-600 text-lg" data-id="element-13938" data-mp="2gzafvei9kdwv">👀</span>
              </div>
              <div data-id="element-13939" data-mp="2vnwx5lrgbfqq">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-13940" data-mp="2vmdgvv5sp4oy">
                  Wat zie je?
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13941" data-mp="2qou28u56qpwh">
                  <p data-id="element-13942" data-mp="34ov2c7dmltaw">
                    Het woord <strong data-id="element-13943" data-mp="34m0l0opwsolp">{word}</strong> bestaat uit{' '}
                    <strong data-id="element-13944" data-mp="2zq1nobmlrw95">{syllables.length}</strong> lettergreep
                    {syllables.length > 1 ? 'en' : ''}:{' '}
                    {syllables.map((s, i) => <span key={i} data-id="element-13945" data-mp="3oaernv49ivaw" data-mpi={i}>
                        <strong className={types[i] === 'open' ? 'text-green-700' : 'text-red-700'} data-id="element-13946" data-mp="3o9d6uyxg2ufz">
                          {s}
                        </strong>
                        {i < syllables.length - 1 ? ' + ' : ''}
                      </span>)}
                  </p>
                  <div className="pt-2 border-t border-blue-100 space-y-1" data-id="element-13947" data-mp="2qmw9jtopogu8">
                    <p className="flex items-center gap-2" data-id="element-13948" data-mp="2vl7midaaz7go">
                      <span className="w-4 h-4 rounded bg-green-500" data-id="element-13949" data-mp="2vlhbpynixalb"></span>
                      <span data-id="element-13950" data-mp="2hmhpsyreb9su">
                        <strong data-id="element-13951" data-mp="2hm843nddvosy">Groen (○):</strong> Open lettergreep = lange
                        klank (eindigt op een klinker)
                      </span>
                    </p>
                    <p className="flex items-center gap-2" data-id="element-13952" data-mp="s5vv8qzyj16t">
                      <span className="w-4 h-4 rounded bg-red-500" data-id="element-13953" data-mp="nd2o5wh0ngrk"></span>
                      <span data-id="element-13954" data-mp="nbrp29yimx6s">
                        <strong data-id="element-13955" data-mp="idq41jhj4nso">Rood (●):</strong> Gesloten lettergreep = korte
                        klank (eindigt op een medeklinker)
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Open vs Closed explanation */}
          <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-13956" data-mp="164lry5iv6yun">
            <div className="flex items-start gap-3" data-id="element-13957" data-mp="12160dnlnjoot">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-13958" data-mp="x3nw4aikxxn5">
                <span className="text-emerald-600 text-lg" data-id="element-13959" data-mp="3mszoyz6xr3pp">📖</span>
              </div>
              <div data-id="element-13960" data-mp="3htwkpgys51is">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-13961" data-mp="3hslezblrxjp1">
                  Open vs Gesloten Lettergrepen
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-3" data-id="element-13962" data-mp="1x9hn4chreu2k">
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200" data-id="element-13963" data-mp="22b71o246xpve">
                    <p className="font-semibold text-green-800 mb-1" data-id="element-13964" data-mp="26bh6vck5sc97">
                      Open lettergreep (○):
                    </p>
                    <p data-id="element-13965" data-mp="2b4tubjd3qd0d">
                      Eindigt op een <strong data-id="element-13966" data-mp="2g2vd036m8abb">klinker</strong> (a, e, i, o, u).
                      De klinker klinkt <strong data-id="element-13967" data-mp="2l0nd3khez2ql">lang</strong>.
                    </p>
                    <p className="text-xs mt-1 text-green-700" data-id="element-13968" data-mp="2l39gr916oy3j">
                      Voorbeeld: <strong data-id="element-13969" data-mp="2q0rhwd8ym90z">ja-ger</strong> (ja = open, lange 'a')
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200" data-id="element-13970" data-mp="2tzqwyqoenm65">
                    <p className="font-semibold text-red-800 mb-1" data-id="element-13971" data-mp="vntd296144l1">
                      Gesloten lettergreep (●):
                    </p>
                    <p data-id="element-13972" data-mp="4dnf85e3bdxl">
                      Eindigt op een <strong data-id="element-13973" data-mp="4cm646gmx1ii">medeklinker</strong>. De klinker
                      klinkt <strong data-id="element-13974" data-mp="cbwmjtmvsyw">kort</strong>.
                    </p>
                    <p className="text-xs mt-1 text-red-700" data-id="element-13975" data-mp="e4ub9m9r2ur">
                      Voorbeeld: <strong data-id="element-13976" data-mp="ebbbizrcgthv">bak-ker</strong> (bak = gesloten, korte
                      'a')
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spelling rule */}
          <div className="bg-indigo-50/80 rounded-xl p-5 border border-indigo-200 shadow-sm" data-id="element-13977" data-mp="3ctgtseov2fc0">
            <div className="flex items-start gap-3" data-id="element-13978" data-mp="3rid3shaha2f9">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-13979" data-mp="3rj4w9j8s3k1b">
                <span className="text-indigo-600 text-lg" data-id="element-13980" data-mp="3mqbvf6gfkvui">✍️</span>
              </div>
              <div data-id="element-13981" data-mp="2z0hhsmoaf397">
                <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-13982" data-mp="2yzzbsybtpao3">
                  Spellingregel
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-13983" data-mp="19l6tqmhoighz">
                  <p data-id="element-13984" data-mp="19j3rn4394www">
                    <strong data-id="element-13985" data-mp="1ebdiqng3s30r">Korte klank (gesloten):</strong> Schrijf meestal{' '}
                    <strong data-id="element-13986" data-mp="10k8nrynojywr">twee medeklinkers</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-13987" data-mp="10ife0ahrsykj">
                    Voorbeeld: bak-ker (korte 'a' → dubbele k), zit-ten (korte
                    'i' → dubbele t)
                  </p>
                  <p className="pt-2 border-t border-indigo-200" data-id="element-13988" data-mp="1xarb50y6p5p5">
                    <strong data-id="element-13989" data-mp="1jaqczt9b0cdd">Lange klank (open):</strong> Schrijf meestal{' '}
                    <strong data-id="element-13990" data-mp="1jcuds2bwov9f">één medeklinker</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-13991" data-mp="1o800h3d4udyk">
                    Voorbeeld: ja-ger (lange 'a' → enkele g), bo-men (lange 'o'
                    → enkele m)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-13992" data-mp="29l0kk8b3w2kx">
            <div className="flex items-start gap-3" data-id="element-13993" data-mp="24u0mv0u5xghd">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-13994" data-mp="24sh6794lh3f2">
                <span className="text-amber-600 text-lg" data-id="element-13995" data-mp="20s6rn82ql4ol">💡</span>
              </div>
              <div data-id="element-13996" data-mp="1vux4jz2tl53x">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-13997" data-mp="1vr9a8xpjweo5">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-13998" data-mp="1lv6a1xe9eq3u">
                  Spreek het woord hardop uit! Hoor je een{' '}
                  <strong data-id="element-13999" data-mp="1huma6m2ax8e0">korte</strong> klinker? Dan is de lettergreep meestal{' '}
                  <strong data-id="element-14000" data-mp="3g9p7hljy23f0">gesloten</strong> en schrijf je{' '}
                  <strong data-id="element-14001" data-mp="3g6urc67yr4re">twee medeklinkers</strong>. Hoor je een{' '}
                  <strong data-id="element-14002" data-mp="3c83rhrc4590l">lange</strong> klinker? Dan is de lettergreep{' '}
                  <strong data-id="element-14003" data-mp="xyrqx9tr27bs">open</strong> en schrijf je{' '}
                  <strong data-id="element-14004" data-mp="xy034l6e9dw2">één medeklinker</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}